/*
  <fd-collection> – progressive enhancement for the collection page.

  Without JS the filter form is a plain GET form and everything still works.
  With JS, filter / sort / pagination changes are fetched through the Section
  Rendering API (same URL + section_id), the results region is swapped, the
  filter form is patched in place (so focus and open groups survive), the URL
  is pushed to history and the new result count is announced.

  Markup contract (see sections/main-collection.liquid):
    [data-fd-filter-form]         the GET form (also owns the sort <select> via form="")
    [data-fd-field]               every input/select that takes part in patching
    [data-fd-region="results"]    swapped on every update
    [data-fd-nav]                 links that should update in place
*/
(function () {
  'use strict';

  if (window.customElements.get('fd-collection')) return;

  var desktop = window.matchMedia('(min-width: 900px)');

  class FDCollection extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.form = this.querySelector('[data-fd-filter-form]');
      this.results = this.querySelector('[data-fd-region="results"]');
      if (!this.form || !this.results) return;

      this._ready = true;
      this.sectionId = this.getAttribute('data-section-id');
      this.dialog = this.querySelector('dialog');
      this.requestId = 0;
      this.controller = null;
      this.timer = null;

      this.setAttribute('data-js', '');
      // Mark the first entry so Back from a pushed state returns to it.
      history.replaceState({ fdCollection: true }, '', location.href);

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.form.addEventListener('submit', this.onSubmit);
      window.addEventListener('popstate', this.onPopState);
      desktop.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      window.clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      window.removeEventListener('popstate', this.onPopState);
      desktop.removeEventListener('change', this.onBreakpoint);
    }

    /* ---------- Events ---------- */

    onChange(event) {
      var field = event.target;
      if (!field.matches || !field.matches('[data-fd-field]')) return;
      // Half-typed prices (e.g. "-") should not trigger a request.
      if (field.validity && !field.validity.valid) return;
      this.schedule(field.tagName === 'SELECT' ? 0 : 250);
    }

    onSubmit(event) {
      event.preventDefault();
      window.clearTimeout(this.timer);
      this.update(this.currentUrl(), { push: true, scroll: true, focus: true });
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    onClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var link = event.target.closest('a[href]');
      if (!link) return;
      var inResults = this.results.contains(link);
      if (!link.hasAttribute('data-fd-nav') && !(inResults && link.closest('.fd-pagination'))) return;

      var url = new URL(link.href, location.href);
      if (url.origin !== location.origin) return;
      event.preventDefault();
      window.clearTimeout(this.timer);
      this.update(url, { push: true, scroll: true, focus: inResults || !this.dialog || !this.dialog.open });
    }

    onPopState(event) {
      if (!event.state || !event.state.fdCollection) return;
      window.clearTimeout(this.timer);
      this.update(new URL(location.href), { push: false });
    }

    onBreakpoint() {
      if (desktop.matches && this.dialog && this.dialog.open) this.dialog.close();
    }

    /* ---------- Request ---------- */

    schedule(delay) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(
        function () { this.update(this.currentUrl(), { push: true, scroll: true }); }.bind(this),
        delay
      );
    }

    currentUrl() {
      var url = new URL(this.form.getAttribute('action') || location.pathname, location.href);
      var params = new URLSearchParams();
      new FormData(this.form).forEach(function (value, key) {
        if (typeof value === 'string' && value !== '') params.append(key, value);
      });
      url.search = params.toString();
      return url;
    }

    update(url, opts) {
      opts = opts || {};
      url = new URL(url.href);
      url.searchParams.delete('section_id');

      var id = ++this.requestId;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.results.setAttribute('aria-busy', 'true');

      var fetchUrl = new URL(url.href);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      var self = this;
      return fetch(fetchUrl.toString(), {
        signal: this.controller.signal,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          if (id !== self.requestId) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.querySelector('fd-collection');
          var nextResults = next && next.querySelector('[data-fd-region="results"]');
          if (!nextResults) throw new Error('Unexpected response');

          // Server-rendered Shopify markup from the same origin.
          self.results.innerHTML = nextResults.innerHTML;
          self.patchFilters(next);
          self.results.removeAttribute('aria-busy');

          if (opts.push !== false) {
            history.pushState({ fdCollection: true }, '', url.pathname + url.search);
          }
          if (window.FD && window.FD.announce) window.FD.announce(nextResults.getAttribute('data-announce'));

          var modalOpen = self.dialog && self.dialog.open;
          if (opts.focus) self.results.focus({ preventScroll: true });
          if (opts.scroll && !modalOpen && self.results.getBoundingClientRect().top < 0) {
            self.results.scrollIntoView({ block: 'start' });
          }
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (id !== self.requestId) return;
          // Anything unexpected: fall back to a normal navigation.
          window.location.assign(url.href);
        });
    }

    /* ---------- Patch the filter form in place ---------- */

    patchFilters(next) {
      var body = this.querySelector('[data-fd-filter-body]');
      var nextBody = next.querySelector('[data-fd-filter-body]');
      var fresh = next.querySelectorAll('[data-fd-field]');
      var current = this.querySelectorAll('[data-fd-field]');

      var sameShape = fresh.length === current.length;
      for (var i = 0; sameShape && i < fresh.length; i++) {
        if (fresh[i].id !== current[i].id) sameShape = false;
      }

      if (!sameShape && body && nextBody) {
        var open = {};
        body.querySelectorAll('[data-fd-group]').forEach(function (group) {
          open[group.getAttribute('data-fd-group')] = group.open;
        });
        body.innerHTML = nextBody.innerHTML;
        body.querySelectorAll('[data-fd-group]').forEach(function (group) {
          var key = group.getAttribute('data-fd-group');
          if (key in open) group.open = open[key];
        });
      }

      var self = this;
      fresh.forEach(function (field) {
        var old = self.querySelector('[id="' + field.id + '"]');
        if (old) self.syncField(old, field);
      });

      next.querySelectorAll('[data-fd-group]').forEach(function (group) {
        var key = group.getAttribute('data-fd-group');
        var old = self.querySelector('[data-fd-group="' + key + '"]');
        if (!old) return;
        var badge = old.querySelector('[data-fd-group-count]');
        var nextBadge = group.querySelector('[data-fd-group-count]');
        if (badge && nextBadge) {
          badge.textContent = nextBadge.textContent;
          badge.hidden = nextBadge.hidden;
        }
      });

      var apply = this.querySelector('[data-fd-apply]');
      var nextApply = next.querySelector('[data-fd-apply]');
      if (apply && nextApply) apply.textContent = nextApply.textContent;

      var badgeEl = this.querySelector('[data-fd-filter-badge]');
      var nextBadgeEl = next.querySelector('[data-fd-filter-badge]');
      if (badgeEl && nextBadgeEl) {
        var num = badgeEl.querySelector('[data-fd-filter-badge-num]');
        var nextNum = nextBadgeEl.querySelector('[data-fd-filter-badge-num]');
        if (num && nextNum) num.textContent = nextNum.textContent;
        badgeEl.hidden = nextBadgeEl.hidden;
      }
    }

    syncField(old, field) {
      if (field.type === 'checkbox' || field.type === 'radio') {
        old.checked = field.checked;
        old.disabled = field.disabled;
        var label = old.closest('.fd-check');
        var nextLabel = field.closest('.fd-check');
        if (label && nextLabel) {
          label.classList.toggle('is-disabled', field.disabled);
          var count = label.querySelector('.fd-check__count');
          var nextCount = nextLabel.querySelector('.fd-check__count');
          if (count && nextCount) count.textContent = nextCount.textContent;
        }
      } else if (field.tagName === 'SELECT') {
        old.value = field.value;
      } else {
        if (document.activeElement !== old) old.value = field.value;
        old.placeholder = field.placeholder;
        old.max = field.max;
      }
    }
  }

  window.customElements.define('fd-collection', FDCollection);
})();
